import { useEffect, useMemo } from 'react';

import {
	useIssueTypesState,
	useMappingsState,
} from '../providers/BulkConfigureContextProvider.tsx';
import { SimpleJiraIssuesSet } from '../__types__/apiUtils.ts';
import { useSetBulkIssuesList } from '../providers/BulkCreateContextProvider.tsx';
import { useIssueCreateSidePanelContext } from '../providers/IssueCreateSidePanelContextProvider.tsx';
import type { InsertStorageColumnTableRequestPayload } from '../__types__/InsertStorageColumnTableRequestPayload.ts';
import { useAiBulkNavigationGotoPage } from '../providers/AiBulkNavigationContextProvider.tsx';

import { mapTableToIssues } from './tableUtils.tsx';

export const useMapTableToIssues = () => {
	const {
		columnToFieldMappings,
		columnOffset,
		mappingWorkItemTypes,
		mappingValidParentCandidates,
		mappingAvailableComponents,
		mappingFieldAllowedValues,
	} = useMappingsState();
	const { selectedIssueType } = useIssueTypesState();
	const [
		{ selectedTable, insertStorageColumnTableRequestPayload },
		{ setInsertStorageColumnTableRequestPayload },
	] = useIssueCreateSidePanelContext();
	const setBulkIssuesList = useSetBulkIssuesList();
	const goToPage = useAiBulkNavigationGotoPage();

	// Memoize the options object to prevent unnecessary re-renders
	const mappingOptions = useMemo(
		() => ({
			mappingWorkItemTypes: mappingWorkItemTypes ?? new Map(),
			selectedIssueType,
			mappingValidParentCandidates: mappingValidParentCandidates ?? new SimpleJiraIssuesSet(),
			mappingFieldAllowedValues: new Map([
				...(mappingFieldAllowedValues ?? new Map()),
				...(mappingAvailableComponents
					? new Map([['components', mappingAvailableComponents]])
					: new Map()),
			]),
		}),
		[
			mappingWorkItemTypes,
			selectedIssueType,
			mappingValidParentCandidates,
			mappingFieldAllowedValues,
			mappingAvailableComponents,
		],
	);

	useEffect(() => {
		if (columnToFieldMappings && columnToFieldMappings.length > 0 && selectedTable) {
			const mappedIssues = mapTableToIssues(columnToFieldMappings, selectedTable, mappingOptions);
			void setBulkIssuesList(mappedIssues);
			if (mappedIssues.length === 0) {
				void goToPage('configure');
			}
		}
	}, [columnToFieldMappings, goToPage, selectedTable, setBulkIssuesList, mappingOptions]);

	useEffect(() => {
		if (columnToFieldMappings && columnToFieldMappings.length > 0) {
			const summaryMapping = columnToFieldMappings.find(
				(mapping) => mapping.field?.key === 'summary',
			);
			//The insertion API does not include header columns in indexing
			const unadjustedIndex = summaryMapping?.column?.index ?? 0;
			const tableColumnIndex =
				columnOffset != null && unadjustedIndex !== 0
					? unadjustedIndex - columnOffset
					: unadjustedIndex;
			const updatedPayload = {
				...insertStorageColumnTableRequestPayload,
				tableColumnIndex,
			} as InsertStorageColumnTableRequestPayload;
			void setInsertStorageColumnTableRequestPayload(updatedPayload);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [columnToFieldMappings]);
};
